import React from 'react';
import { useNavigation } from '../context/NavigationContext';
import { legalConfig, isFieldPlaceholder } from '../../config/legalConfig';
import { ShoppingBag, ShieldCheck, Calendar, ArrowLeft, Clock, AlertTriangle } from 'lucide-react';

export const DistanceSalesPage: React.FC = () => {
  const { navigate } = useNavigation();

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Back Link */}
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-2 text-xs font-semibold text-slate-400 hover:text-white transition-colors mb-8 group"
        >
          <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-0.5" />
          <span>Ana Sayfaya Dön</span>
        </button>

        {/* Header */}
        <div className="border-b border-slate-800 pb-8 mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-sky-500/10 text-sky-400 border border-sky-500/20 mb-4">
            <ShoppingBag className="w-3.5 h-3.5" />
            <span>Tüketici Bilgilendirmesi</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight leading-tight">
            ÖN BİLGİLENDİRME FORMU VE MESAFELİ SATIŞ SÖZLEŞMESİ
          </h1>
          <div className="mt-4 flex flex-wrap items-center gap-6 text-xs text-slate-400">
            <span className="flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 text-sky-400" />
              <span>Sağlayıcı: <strong className="text-slate-200">{legalConfig.companyName}</strong></span>
            </span>
            <span className="flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5 text-sky-400" />
              <span>Yürürlük Tarihi: <strong className="text-slate-200">{legalConfig.effectiveDate}</strong></span>
            </span>
            <span className="text-slate-500">Sürüm: {legalConfig.distanceSalesVersion}</span>
          </div>
          <p className="mt-4 text-sm text-slate-300 leading-relaxed">
            {legalConfig.relationshipStatement} İşbu metin; 6502 sayılı Tüketicinin Korunması Hakkında Kanun ve Mesafeli Sözleşmeler Yönetmeliği hükümleri uyarınca, tüketicinin sipariş ve ödeme yükümlülüğü altına girmeden önce bilgilendirilmesi amacıyla hazırlanmıştır.
          </p>
        </div>

        {/* Content */}
        <div className="prose prose-invert prose-slate max-w-none text-sm leading-relaxed space-y-8 text-slate-300">
          {/* 1. Taraflar */}
          <section className="bg-slate-900/50 p-6 rounded-2xl border border-slate-800 space-y-3">
            <h2 className="text-base font-bold text-white">1. Taraflar ve Sağlayıcı Bilgileri</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
              <div>
                <p className="font-semibold text-slate-200">SAĞLAYICI (Hizmet Sunan):</p>
                <p>Unvan: <strong className="text-white">{legalConfig.companyName}</strong></p>
                <p>Marka: {legalConfig.brandName}</p>
                <p>Merkez Adres: {legalConfig.registeredOffice}</p>
                <p>Kayıt Ülkesi: {legalConfig.registrationCountry}</p>
                <p>Sicil / Kayıt No: {legalConfig.registrationNumber}</p>
                <p>Vergi No: {legalConfig.taxNumber}</p>
                <p>E-Posta: {legalConfig.email}</p>
                <p>Telefon: {legalConfig.phone}</p>
              </div>
              <div>
                <p className="font-semibold text-slate-200">ALICI (Tüketici / Müşteri):</p>
                <p>Platform üzerinden hesap oluşturan, fatura ve iletişim bilgilerini sipariş ekranında beyan eden Kullanıcıdır.</p>
              </div>
            </div>
          </section>

          {/* 2. Hizmetin Temel Nitelikleri */}
          <section className="space-y-3">
            <h2 className="text-base font-bold text-white">2. Hizmetin Temel Nitelikleri ve Kapsamı</h2>
            <p>
              Sözleşme konusu hizmet; Alıcı&apos;nın seçtiği dijital paket doğrultusunda Adiosoft Platformu üzerinden görsel web sitesi editörü, sektörel modüller, özel alan adı bağlama, AI Local Visibility analiz aracı ve/veya lisans ve altyapı bağlantı hizmetlerine süreli dijital erişim sağlanmasıdır.
            </p>
          </section>

          {/* 3. Lisans Süresi ve Süreli Nitelik */}
          <section className="bg-blue-950/20 p-5 rounded-2xl border border-blue-500/30 space-y-2">
            <h2 className="text-base font-bold text-white flex items-center gap-2">
              <Clock className="w-4 h-4 text-blue-400" />
              <span>3. Lisans Süresi ve Süreli Dijital Hizmet Niteliği</span>
            </h2>
            <p className="text-slate-200">
              Satın alınan hizmet; <strong className="text-white">seçilen lisans/abonelik dönemi boyunca geçerli süreli bir kullanım hakkıdır</strong> (örneğin 1 yıllık paket için başlangıç tarihinden itibaren 12 takvim ayı).
            </p>
            <p className="text-slate-300">
              Bu işlem kaynak kodlarının veya yazılım mülkiyetinin devri anlamına gelmez. Dönem bitiminde yenileme yapılmadığı takdirde web sitesinin yayınlanma ve düzenleme hakları pasife alınır.
            </p>
          </section>

          {/* 4. Toplam Fiyat ve Ödeme Yükümlülüğü */}
          <section className="space-y-3">
            <h2 className="text-base font-bold text-white">4. Toplam Fiyat ve Ödeme Bilgilendirmesi</h2>
            <p>
              Hizmetin tüm vergiler (KDV) dahil toplam satış bedeli, seçilen paketin türüne göre sipariş ve ödeme ekranında açıkça gösterilmektedir. Ödeme işlemi tamamlandığında Kullanıcı kesin bir ödeme yükümlülüğü altına girer. Tahsilat, lisanslı ödeme altyapısı Shopier aracılığıyla kredi kartı/banka kartı ile güvenli şekilde gerçekleştirilir.
            </p>
          </section>

          {/* 5. İfa Başlangıcı ve Cayma Hakkı İstisnası */}
          <section className="bg-slate-900/60 p-5 rounded-2xl border border-slate-800 space-y-3">
            <h2 className="text-base font-bold text-white flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-400" />
              <span>5. İfa Başlangıcı, Cayma Hakkı ve Kanuni İstisnalar</span>
            </h2>
            <p className="text-xs text-slate-300">
              6502 sayılı Kanun uyarınca mesafeli hizmet sözleşmelerinde genel kural 14 günlük cayma hakkıdır. Ancak Mesafeli Sözleşmeler Yönetmeliği m. 15/1-ğ bendi gereğince:
            </p>
            <div className="p-3.5 rounded-xl bg-slate-950 border border-slate-800 text-xs text-amber-300/90 font-medium">
              &quot;Elektronik ortamda anında ifa edilen hizmetler veya tüketiciye anında teslim edilen gayrimaddi mallara ilişkin sözleşmelerde cayma hakkı kullanılamaz.&quot;
            </div>
            <p className="text-xs text-slate-400 leading-relaxed">
              Kullanıcı, satın alma aşamasında hizmetin derhal elektronik ortamda başlatılmasını talep ettiğinde ve dijital lisans anında aktif edildiğinde bu kanuni istisna geçerli olur. Bununla birlikte, 6502 sayılı Kanun kapsamında ayıplı hizmete ilişkin tüketici hakları ve kanuni zorunlu iade hakları tam olarak saklıdır.
            </p>
          </section>

          {/* 6. Uyuşmazlık Çözümü */}
          <section className="space-y-3">
            <h2 className="text-base font-bold text-white">6. Uyuşmazlık Çözümü ve Kayıtların Saklanması</h2>
            <p>
              Tüketici, uyuşmazlık halinde ikametgâhının bulunduğu yerdeki veya tüketici işleminin yapıldığı yerdeki Tüketici Hakem Heyetine veya Tüketici Mahkemesine başvurabilir. İşbu mesafeli satış sözleşmesine ilişkin sipariş, onay, fatura ve elektronik kayıtlar mevzuat gereğince en az 3 (üç) yıl süreyle saklanır.
            </p>
          </section>
        </div>

        {/* Company Contact */}
        <div className="mt-12 p-6 rounded-2xl bg-slate-900 border border-slate-800 text-xs text-slate-400 space-y-2">
          <p className="font-semibold text-slate-200">Sağlayıcı İletişim Bilgileri:</p>
          <p>Unvan: <strong className="text-white">{legalConfig.companyName}</strong></p>
          <p>Adres: {legalConfig.registeredOffice}</p>
          <p>E-Posta: {legalConfig.email}</p>
          <p>Telefon: {legalConfig.phone}</p>
        </div>
      </div>
    </div>
  );
};
export default DistanceSalesPage;
